import React, { useMemo, useState } from "react";
import { ChevronRight, MapPin, Car, CalendarClock, Navigation } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { STATUS_KEYS, STATUS_META, PRECISION_LABEL, PRECISION_CHIP } from "@/components/territory/statusMeta";

const UNKNOWN = {
  city: "Unknown city",
  area: "Unknown area",
  subregion: "Unknown sub-area",
  suburb: "Unknown suburb",
};

const km = (a, b) => {
  const R = 6371;
  const toRad = (v) => (v * Math.PI) / 180;
  const dLat = toRad(b.latitude - a.latitude);
  const dLon = toRad(b.longitude - a.longitude);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.latitude)) * Math.cos(toRad(b.latitude)) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const StatusBar = ({ counts, total }) => (
  <div className="h-1.5 w-24 rounded bg-muted overflow-hidden flex shrink-0">
    {STATUS_KEYS.map((key) =>
      counts[key] ? (
        <div key={key} className={STATUS_META[key].bar} style={{ width: `${(counts[key] / total) * 100}%` }} />
      ) : null
    )}
  </div>
);

const tally = (rows) => {
  const counts = { green: 0, yellow: 0, blue: 0, red: 0 };
  rows.forEach((r) => {
    counts[r.status] += 1;
  });
  return counts;
};

/** City → Area → Sub-area → Suburb → doctor cards. Every level counts its own status mix. */
export const DoctorDrillDown = ({ doctors = [], onSelectDoctor, selected = [] }) => {
  const [open, setOpen] = useState({});
  const toggle = (key) => setOpen((prev) => ({ ...prev, [key]: !prev[key] }));

  const tree = useMemo(() => {
    const root = new Map();
    doctors.forEach((d) => {
      const city = d.city || UNKNOWN.city;
      const area = d.area || UNKNOWN.area;
      const sub = d.subregion || UNKNOWN.subregion;
      const suburb = d.suburb || UNKNOWN.suburb;
      if (!root.has(city)) root.set(city, new Map());
      const areas = root.get(city);
      if (!areas.has(area)) areas.set(area, new Map());
      const subs = areas.get(area);
      if (!subs.has(sub)) subs.set(sub, new Map());
      const suburbs = subs.get(sub);
      if (!suburbs.has(suburb)) suburbs.set(suburb, []);
      suburbs.get(suburb).push(d);
    });
    return root;
  }, [doctors]);

  const nearest = useMemo(() => {
    const out = {};
    const bySuburb = {};
    doctors.forEach((d) => {
      if (d.latitude == null) return;
      const key = `${d.city}|${d.area}|${d.suburb}`;
      (bySuburb[key] = bySuburb[key] || []).push(d);
    });
    Object.values(bySuburb).forEach((group) => {
      group.forEach((d) => {
        let best = null;
        group.forEach((other) => {
          if (other.doctor_id === d.doctor_id) return;
          const distance = km(d, other);
          if (!best || distance < best.distance) best = { distance, name: other.doctor_name };
        });
        if (best) out[d.doctor_id] = best;
      });
    });
    return out;
  }, [doctors]);

  if (doctors.length === 0) {
    return <p className="text-sm text-muted-foreground py-8 text-center">No doctors match these filters.</p>;
  }

  const row = (label, rows, level, key, children) => {
    const counts = tally(rows);
    return (
      <div key={key} className={level > 0 ? "border-l ml-3" : ""}>
        <button
          onClick={() => toggle(key)}
          className="w-full flex items-center gap-2 py-2 pr-2 pl-2 hover:bg-muted/60 rounded text-left"
          data-testid={`drilldown-${level}-${label.replace(/\s+/g, "-").toLowerCase()}`}
        >
          <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${open[key] ? "rotate-90" : ""}`} />
          <span className={`truncate ${level === 0 ? "font-semibold text-sm" : "text-sm"}`}>{label}</span>
          <span className="text-xs text-muted-foreground shrink-0">{rows.length}</span>
          <span className="flex-1" />
          <StatusBar counts={counts} total={rows.length} />
        </button>
        {open[key] && <div className="pb-1">{children}</div>}
      </div>
    );
  };

  return (
    <div className="space-y-1" data-testid="doctor-drilldown">
      {[...tree.entries()].map(([city, areas]) => {
        const cityRows = [...areas.values()].flatMap((subs) => [...subs.values()].flatMap((s) => [...s.values()].flat()));
        return row(
          city,
          cityRows,
          0,
          `city:${city}`,
          [...areas.entries()].map(([area, subs]) => {
            const areaRows = [...subs.values()].flatMap((s) => [...s.values()].flat());
            return row(
              area,
              areaRows,
              1,
              `area:${city}:${area}`,
              [...subs.entries()].map(([sub, suburbs]) => {
                const subRows = [...suburbs.values()].flat();
                return row(
                  sub,
                  subRows,
                  2,
                  `sub:${city}:${area}:${sub}`,
                  [...suburbs.entries()].map(([suburb, members]) =>
                    row(
                      suburb,
                      members,
                      3,
                      `suburb:${city}:${area}:${sub}:${suburb}`,
                      <div className="ml-6 space-y-1.5 py-1">
                        {members.map((doctor) => {
                          const meta = STATUS_META[doctor.status];
                          const near = nearest[doctor.doctor_id];
                          return (
                            <div
                              key={doctor.doctor_id}
                              onClick={() => onSelectDoctor?.(doctor.doctor_id)}
                              className={`rounded-md border border-l-4 ${meta.accent} p-2.5 cursor-pointer transition-colors ${
                                selected.includes(doctor.doctor_id) ? "bg-blue-50/70 dark:bg-blue-950/30" : "hover:bg-muted/50"
                              }`}
                              data-testid={`drilldown-doctor-${doctor.doctor_id}`}
                            >
                              <div className="flex flex-wrap items-center gap-2">
                                <p className="font-semibold text-sm">{doctor.doctor_name}</p>
                                <Badge className={`text-[10px] hover:bg-inherit ${meta.chip}`}>{meta.short}</Badge>
                                <Badge variant="outline" className="text-[10px]">
                                  {doctor.tier}
                                </Badge>
                                {doctor.geocode_precision && (
                                  <Badge
                                    className={`text-[10px] hover:bg-inherit ${PRECISION_CHIP[doctor.geocode_precision] || ""}`}
                                  >
                                    {PRECISION_LABEL[doctor.geocode_precision] || doctor.geocode_precision}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3 shrink-0" />
                                {doctor.address || "No street address on record"}
                              </p>
                              <div className="flex flex-wrap items-center gap-3 mt-1 text-xs">
                                <span className="flex items-center gap-1 text-blue-700 dark:text-blue-300">
                                  <Car className="w-3 h-3" />
                                  {doctor.visits_90days + doctor.appointments_90days} contacts in 90d
                                </span>
                                <span className="flex items-center gap-1 text-violet-700 dark:text-violet-300">
                                  <CalendarClock className="w-3 h-3" />
                                  last {doctor.last_contact || "never"}
                                </span>
                                <span className="text-muted-foreground">
                                  {doctor.referrals_90days} referrals in 90d · {doctor.referral_count} all time
                                </span>
                                {near && (
                                  <span className="flex items-center gap-1 text-muted-foreground">
                                    <Navigation className="w-3 h-3" />
                                    {near.distance < 0.1 ? "same pin as" : `${near.distance.toFixed(1)}km to`}{" "}
                                    {near.name}
                                  </span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )
                  )
                );
              })
            );
          })
        );
      })}
    </div>
  );
};

export default DoctorDrillDown;
